import React, { useState } from 'react';
import { collection, addDoc, writeBatch, doc, serverTimestamp } from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { db, secondaryAuth } from '../firebaseConfig'; // Usar secondaryAuth para no cerrar sesión
import { useUser } from '../context/UserContext';

interface MigrationLog {
  timestamp: Date;
  type: 'info' | 'success' | 'warning' | 'error';
  message: string;
}

interface MigrationResults {
  agencias: { exitosas: number; fallidas: number; total: number };
  miembros: { exitosos: number; fallidos: number; total: number };
  propiedades: { exitosas: number; fallidas: number; total: number };
}

export default function AMPIMigrationOptimized() {
  const { user } = useUser();
  const [isRunning, setIsRunning] = useState(false);
  const [logs, setLogs] = useState<MigrationLog[]>([]);
  const [results, setResults] = useState<MigrationResults | null>(null);
  const [csvAgencias, setCsvAgencias] = useState<string>('');
  const [csvMiembros, setCsvMiembros] = useState<string>('');
  const [csvPropiedades, setCsvPropiedades] = useState<string>('');
  const [migrationMode, setMigrationMode] = useState<'completa' | 'agencias' | 'miembros' | 'propiedades'>('completa');

  // Solo admins pueden acceder
  if (!user || !user.tipo?.includes('admin')) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">
          <h4>Acceso Denegado</h4>
          <p>Solo administradores pueden ejecutar la migración AMPI.</p>
        </div>
      </div>
    );
  }

  const addLog = (type: 'info' | 'success' | 'warning' | 'error', message: string) => {
    setLogs(prev => [...prev, {
      timestamp: new Date(),
      type,
      message
    }]);
    console.log(`[${type.toUpperCase()}] ${message}`);
  };

  const handleFileRead = (file: File, setterCallback: (content: string) => void) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setterCallback(content);
      addLog('success', `Archivo cargado: ${file.name} (${(file.size / 1024).toFixed(1)} KB)`);
    };
    reader.readAsText(file, 'UTF-8');
  };

  // Parser CSV mejorado (maneja comillas y comas dentro de campos)
  const parseCSV = (csvText: string): any[] => {
    const lines = csvText.trim().split('\n');
    if (lines.length < 2) return [];

    // Función para parsear una línea CSV respetando comillas
    const parseCsvLine = (line: string): string[] => {
      const result: string[] = [];
      let current = '';
      let inQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];

        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      result.push(current.trim());
      return result;
    };

    const headers = parseCsvLine(lines[0]);
    const data: any[] = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      if (!line.trim()) continue;

      const values = parseCsvLine(line);
      const row: any = {};

      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });

      data.push(row);
    }

    addLog('info', `   📋 CSV parseado: ${headers.length} columnas, ${data.length} filas`);
    return data;
  };

  // Parser JSON (para propiedades)
  const parseJSON = (jsonText: string): any[] => {
    try {
      const data = JSON.parse(jsonText);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      addLog('error', `❌ Error al parsear JSON: ${error}`);
      return [];
    }
  };

  const procesarAgencias = async (agenciasData: any[]) => {
    addLog('info', `📍 FASE 1: Procesando ${agenciasData.length} agencias (→ colección 'inmobiliaria')...`);

    // Mapear columnas si vienen del CSV de agencias-ampi
    const agenciasProcesadas = agenciasData.map(a => ({
      id: a.id || normalizarId(a.Nombre || a.nombre || ''),
      nombre: a.Nombre || a.nombre || '',
      descripcion: a.Descripcion || a.descripcion || '',
      correo: a.Correo || a.correo || '',
      telefono: a.Telefono || a.telefono || '',
      direccion: a.Address || a.direccion || '',
      ciudad: a.Ciudad || a.ciudad || '',
      estado: a.Estado || a.estado || '',
      codigoPostal: a.CodigoPostal || a.codigoPostal || '',
      sitioWeb: a.SitioWeb || a.sitioWeb || '',
      facebook: a.Facebook || a.facebook || '',
      instagram: a.Instagram || a.instagram || '',
      lat: parseFloat(a.Lat || a.lat || '0'),
      lng: parseFloat(a.Lng || a.lng || '0')
    }));

    let exitosas = 0;
    let fallidas = 0;
    const batchSize = 500;

    for (let i = 0; i < agenciasProcesadas.length; i += batchSize) {
      const batch = writeBatch(db);
      const lote = agenciasProcesadas.slice(i, Math.min(i + batchSize, agenciasProcesadas.length));

      for (const agencia of lote) {
        try {
          if (!agencia.id || !agencia.nombre) {
            addLog('warning', `⚠️ Agencia sin ID o nombre: ${JSON.stringify(agencia)}`);
            fallidas++;
            continue;
          }

          const docRef = doc(collection(db, 'inmobiliaria'));

          const inmobiliariaDoc = {
            // ID de negocio
            inmobiliaria_id: agencia.id,

            // Información básica
            nombre: agencia.nombre,
            descripcion: agencia.descripcion || '',

            // Contacto
            correo: agencia.correo || '',
            telefono: agencia.telefono || '',
            direccion: agencia.direccion || agencia.ciudad || '',

            // Web
            sitio_web: agencia.sitioWeb || '',

            // Redes sociales
            redes: {
              facebook: agencia.facebook || '',
              instagram: agencia.instagram || '',
              twitter: '',
              linkedin: '',
              youtube: '',
              tiktok: ''
            },

            // Coordenadas
            coordenadas: {
              lat: agencia.lat || 0,
              lng: agencia.lng || 0,
              validas: (agencia.lat && agencia.lng) ? true : false
            },

            // Metadatos
            anos_experiencia: 0,
            status: 'ACTIVE',
            fecha_creacion: serverTimestamp(),

            // Referencias (arrays para asesores, asociados, propiedades)
            asesor_id: [],
            asociado_id: [],
            propiedad_id: [],
            proyecto_id: []
          };

          batch.set(docRef, inmobiliariaDoc);
          exitosas++;

        } catch (error: any) {
          addLog('error', `❌ Error en agencia ${agencia.nombre}: ${error.message}`);
          fallidas++;
        }
      }

      try {
        await batch.commit();
        addLog('success', `✅ Lote ${Math.floor(i / batchSize) + 1} procesado: ${lote.length} agencias`);
      } catch (error: any) {
        addLog('error', `❌ Error al guardar lote: ${error.message}`);
        fallidas += lote.length;
        exitosas -= lote.length;
      }
    }

    addLog('success', `✅ Agencias completadas: ${exitosas} exitosas, ${fallidas} fallidas`);
    return { exitosas, fallidas, total: agenciasProcesadas.length };
  };

  // Función para normalizar texto y generar IDs
  const normalizarId = (texto: string): string => {
    return texto
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Elimina acentos
      .replace(/[^a-z0-9]+/g, '-')      // Reemplaza no-alfanuméricos con -
      .replace(/^-+|-+$/g, '');         // Elimina guiones al inicio/fin
  };

  // Función para crear usuario en Firebase Auth (usando instancia secundaria para no cerrar sesión)
  const crearUsuarioAuth = async (correo: string, nombre: string): Promise<string | null> => {
    try {
      if (!correo || !correo.includes('@')) {
        return null;
      }

      const password = 'AMPIRiviera2024!'; // Contraseña temporal
      // Usar secondaryAuth para NO afectar la sesión actual del admin
      const userCredential = await createUserWithEmailAndPassword(secondaryAuth, correo.toLowerCase().trim(), password);
      const uid = userCredential.user.uid;
      addLog('success', `   ✅ Usuario Auth creado: ${correo} (UID: ${uid})`);
      return uid;
    } catch (error: any) {
      if (error.code === 'auth/email-already-in-use') {
        addLog('info', `   ℹ️ Usuario ya existe: ${correo}`);
        // Obtener UID del usuario existente
        try {
          const { signInWithEmailAndPassword } = await import('firebase/auth');
          const password = 'AMPIRiviera2024!';
          const credential = await signInWithEmailAndPassword(secondaryAuth, correo.toLowerCase().trim(), password);
          const uid = credential.user.uid;
          addLog('info', `   ℹ️ UID obtenido: ${uid}`);
          return uid;
        } catch {
          addLog('warning', `   ⚠️ No se pudo obtener UID para ${correo}`);
          return null;
        }
      }
      addLog('warning', `   ⚠️ No se pudo crear usuario ${correo}: ${error.message}`);
      return null;
    }
  };

  const procesarMiembros = async (miembrosData: any[]) => {
    addLog('info', `📍 FASE 2: Procesando ${miembrosData.length} miembros...`);

    // Mapear columnas del CSV de entrada a las esperadas
    const miembrosProcesados = miembrosData.map(m => {
      // Generar ID a partir del nombre
      const nombre = m.Nombre || m.nombre || '';
      const tipo = m.Tipo || m.tipo || 'Afiliado';
      const correo = m.Correo || m['Correo-Flex'] || m.correo || '';
      const agenciaNombre = m.Agencia || m.agencia || '';

      return {
        id: normalizarId(nombre),
        nombre: nombre,
        nombreEnFlex: m['Nombre en Flex'] || nombre,
        tipo: tipo,
        correo: correo.toLowerCase().trim(),
        telefono: m.Telefono || m.telefono || '',
        agenciaId: normalizarId(agenciaNombre),
        agenciaNombre: agenciaNombre,
        sitioWeb: m.SitioWeb || m.sitioWeb || '',
        certificaciones: m['Certificaciones/Designations'] || '',
        facebook: m.Facebook || '',
        instagram: m.Instagram || '',
        biografia: m.Biografia || '',
        anosExperiencia: parseInt(m['Anios Experiencia'] || '0') || 0
      };
    });

    // Separar por tipo
    const afiliados = miembrosProcesados.filter(m => m.tipo === 'Afiliado');
    const asociados = miembrosProcesados.filter(m => m.tipo === 'Asociado');

    addLog('info', `   📊 ${afiliados.length} Afiliados (→ colección 'asesor')`);
    addLog('info', `   📊 ${asociados.length} Asociados (→ colección 'asociado')`);
    addLog('info', `   🔐 Se crearán usuarios en Firebase Authentication con correo`);

    let exitosos = 0;
    let fallidos = 0;
    let usuariosCreados = 0;
    const batchSize = 500;

    // Procesar AFILIADOS → colección 'asesor'
    for (let i = 0; i < afiliados.length; i += batchSize) {
      const batch = writeBatch(db);
      const lote = afiliados.slice(i, Math.min(i + batchSize, afiliados.length));

      for (const miembro of lote) {
        try {
          if (!miembro.id || !miembro.nombre) {
            addLog('warning', `⚠️ Afiliado sin ID o nombre: ${JSON.stringify(miembro)}`);
            fallidos++;
            continue;
          }

          // Crear usuario en Firebase Authentication y obtener UID
          let uidAuth = null;
          if (miembro.correo) {
            uidAuth = await crearUsuarioAuth(miembro.correo, miembro.nombre);
            if (uidAuth) usuariosCreados++;
          }

          const docRef = doc(collection(db, 'asesor'));

          const asesorDoc = {
            // ID de negocio (UID de Firebase Auth si existe, sino ID generado)
            asesor_id: uidAuth || miembro.id,

            // Información personal
            nombre: miembro.nombre,
            nombreEnFlex: miembro.nombreEnFlex,
            correo: miembro.correo,
            telefono: miembro.telefono || '',
            tipo: 'asesor',

            // Agencia (array para compatibilidad con sistema actual)
            inmobiliaria_id: miembro.agenciaId ? [miembro.agenciaId] : [],

            // Web
            sitio_web: miembro.sitioWeb || '',

            // Metadatos
            activo: true,
            calificacion: 0,
            nivel_calificacion: 'Nuevo',
            biografia: miembro.biografia || '',
            anos_experiencia: miembro.anosExperiencia || 0,
            certificaciones: miembro.certificaciones ? miembro.certificaciones.split(',').map((c: string) => c.trim()) : [],

            // Rating breakdown (estructura actual del sistema)
            breakdown_rating: {
              cursos: 0,
              experiencia: 0,
              opiniones: 0,
              participacion: 0,
              perfil: 0
            },

            // Referencias
            propiedad_id: [],

            // Redes sociales
            redes: {
              facebook: miembro.facebook || '',
              instagram: miembro.instagram || ''
            },

            // Fechas
            fecha_creacion: serverTimestamp()
          };

          batch.set(docRef, asesorDoc);
          exitosos++;

        } catch (error: any) {
          addLog('error', `❌ Error en afiliado ${miembro.nombre}: ${error.message}`);
          fallidos++;
        }
      }

      try {
        await batch.commit();
        addLog('success', `✅ Lote afiliados ${Math.floor(i / batchSize) + 1}: ${lote.length} procesados`);
      } catch (error: any) {
        addLog('error', `❌ Error al guardar lote afiliados: ${error.message}`);
        fallidos += lote.length;
        exitosos -= lote.length;
      }
    }

    // Procesar ASOCIADOS → colección 'asociado'
    for (let i = 0; i < asociados.length; i += batchSize) {
      const batch = writeBatch(db);
      const lote = asociados.slice(i, Math.min(i + batchSize, asociados.length));

      for (const miembro of lote) {
        try {
          if (!miembro.id || !miembro.nombre) {
            addLog('warning', `⚠️ Asociado sin ID o nombre: ${JSON.stringify(miembro)}`);
            fallidos++;
            continue;
          }

          // Crear usuario en Firebase Authentication y obtener UID
          let uidAuth = null;
          if (miembro.correo) {
            uidAuth = await crearUsuarioAuth(miembro.correo, miembro.nombre);
            if (uidAuth) usuariosCreados++;
          }

          const docRef = doc(collection(db, 'asociado'));

          const asociadoDoc = {
            // ID de negocio (UID de Firebase Auth si existe, sino ID generado)
            asociado_id: uidAuth || miembro.id,

            // Información personal
            nombre: miembro.nombre,
            nombreEnFlex: miembro.nombreEnFlex,
            correo: miembro.correo,
            telefono: miembro.telefono || '',
            tipo: 'asociado',

            // Agencia (string para asociados)
            inmobiliaria_id: miembro.agenciaId || '',

            // Web
            sitio_web: miembro.sitioWeb || '',

            // Metadatos
            activo: true,
            calificacion: 0,
            nivel_calificacion: 'Nuevo',
            biografia: miembro.biografia || '',
            anos_experiencia: miembro.anosExperiencia || 0,
            certificaciones: miembro.certificaciones ? miembro.certificaciones.split(',').map((c: string) => c.trim()) : [],

            // Rating breakdown
            breakdown_rating: {
              cursos: 0,
              experiencia: 0,
              opiniones: 0,
              participacion: 0,
              perfil: 0
            },

            // Redes sociales
            redes: {
              facebook: miembro.facebook || '',
              instagram: miembro.instagram || ''
            },

            // Fechas
            fecha_creacion: serverTimestamp()
          };

          batch.set(docRef, asociadoDoc);
          exitosos++;

        } catch (error: any) {
          addLog('error', `❌ Error en asociado ${miembro.nombre}: ${error.message}`);
          fallidos++;
        }
      }

      try {
        await batch.commit();
        addLog('success', `✅ Lote asociados ${Math.floor(i / batchSize) + 1}: ${lote.length} procesados`);
      } catch (error: any) {
        addLog('error', `❌ Error al guardar lote asociados: ${error.message}`);
        fallidos += lote.length;
        exitosos -= lote.length;
      }
    }

    addLog('success', `✅ Miembros completados: ${exitosos} exitosos (${afiliados.length} afiliados + ${asociados.length} asociados), ${fallidos} fallidos`);
    addLog('success', `🔐 Usuarios Authentication creados: ${usuariosCreados} (contraseña: AMPIRiviera2024!)`);
    return { exitosos, fallidos, total: miembrosProcesados.length };
  };

  const procesarPropiedades = async (propiedadesData: any[]) => {
    addLog('info', `📍 FASE 3: Procesando ${propiedadesData.length} propiedades...`);

    let exitosas = 0;
    let fallidas = 0;
    const batchSize = 500;

    for (let i = 0; i < propiedadesData.length; i += batchSize) {
      const batch = writeBatch(db);
      const lote = propiedadesData.slice(i, Math.min(i + batchSize, propiedadesData.length));

      for (const propiedad of lote) {
        try {
          if (!propiedad.id && !propiedad.ID) {
            addLog('warning', `⚠️ Propiedad sin ID`);
            fallidas++;
            continue;
          }

          const docRef = doc(collection(db, 'propiedades'));

          const propiedadDoc = {
            // IDs y básicos
            id: propiedad.id || propiedad.ID,
            list_number: propiedad.listNumber || '',
            titulo: propiedad.titulo || propiedad.Titulo || propiedad['Título'] || '',

            // APLANAR características para acceso rápido en el frontend
            recamaras: propiedad.caracteristicas?.recamaras || 0,
            banosCompletos: propiedad.caracteristicas?.banosCompletos || 0,
            mediosBanos: propiedad.caracteristicas?.mediosBanos || 0,
            banosTotales: (propiedad.caracteristicas?.banosCompletos || 0) +
                          (propiedad.caracteristicas?.mediosBanos || 0) * 0.5,
            totalM2: propiedad.caracteristicas?.totalM2 || 0,
            totalSqFt: propiedad.caracteristicas?.totalSqFt || 0,
            estacionamientos: propiedad.caracteristicas?.estacionamientos || '',
            pisos: propiedad.caracteristicas?.pisos || '',
            anosConstruccion: propiedad.caracteristicas?.anosConstruccion || '',
            amueblado: propiedad.caracteristicas?.amueblado || '',
            mascotas: propiedad.caracteristicas?.mascotas === 'Y',
            vistasPrimarias: propiedad.caracteristicas?.vistasPrimarias || '',
            vistasSecundarias: propiedad.caracteristicas?.vistasSecundarias || '',

            // APLANAR precios
            precio: propiedad.precios?.listaUSD || propiedad.Precio || 0,
            precioUSD: propiedad.precios?.listaUSD || propiedad.Precio || 0,
            precioMXN: propiedad.precios?.listaMXN || 0,
            precioOriginalUSD: propiedad.precios?.originalUSD || 0,
            precioPorM2: propiedad.precios?.precioPorM2 || 0,

            // APLANAR ubicación
            area: propiedad.ubicacion?.area || '',
            calle: propiedad.ubicacion?.calle || '',
            numeroExterior: propiedad.ubicacion?.numeroExterior || '',
            zona: propiedad.ubicacion?.zona || 'Riviera Nayarit',
            estadoGeografico: propiedad.ubicacion?.estado || 'Nayarit',
            comunidad: propiedad.ubicacion?.comunidad || '',
            nombrePropiedad: propiedad.ubicacion?.nombrePropiedad || '',
            coordenadas: propiedad.ubicacion?.coordenadas || { lat: 0, lng: 0, validas: false },

            // Terreno
            lotM2: propiedad.terreno?.lotM2 || 0,
            lotSqFt: propiedad.terreno?.lotSqFt || 0,

            // Agente y agencia
            agente: {
              nombre: propiedad.agenteListingNombre || propiedad.Agente || '',
              email: propiedad['Email Agente'] || ''
            },
            agencia: propiedad.agenciaNombre || propiedad.Agencia || '',
            inmobiliaria_id: propiedad.agenciaId || '',
            asesor_id: [],  // Se llenará en sincronización

            // Tipo y estado
            tipo: propiedad.tipo || propiedad.Tipo || 'Residential',
            formato: propiedad.formato || '',
            estado: propiedad.estado === 'A' ? 'ACTIVO' : 'INACTIVO',
            activo: propiedad.estado === 'A',
            destacada: false,

            // Descripción y fotos
            descripcion: propiedad.descripcion || '',
            observacionesPrivadas: propiedad.observacionesPrivadas || '',
            photoUrl: propiedad.photoUrl || '',
            fotos: propiedad.photoUrl ? [propiedad.photoUrl] : [],

            // Features
            features: propiedad.features || [],

            // HOA
            hoa: propiedad.hoa || { cuotaMensual: 0 },

            // Legal
            escritura: propiedad.legal?.escritura || '',
            numeroPredial: propiedad.legal?.numeroPredial || '',

            // Objetos completos (para referencia futura y compatibilidad)
            caracteristicas: propiedad.caracteristicas || {},
            precios: propiedad.precios || {},
            ubicacion: propiedad.ubicacion || {},
            terreno: propiedad.terreno || {},
            legal: propiedad.legal || {},

            // Metadata
            metadata: {
              fuente: 'migracion-riviera-nayarit-2025-10-06',
              zona: 'Riviera Nayarit',
              diasEnMercado: propiedad.metadata?.diasEnMercado || 0
            },

            // Fechas
            fecha_creacion: serverTimestamp(),
            fecha_actualizacion: serverTimestamp()
          };

          batch.set(docRef, propiedadDoc);
          exitosas++;

          if ((exitosas + fallidas) % 50 === 0) {
            addLog('info', `⏳ Progreso: ${exitosas + fallidas}/${propiedadesData.length} propiedades procesadas`);
          }

        } catch (error: any) {
          addLog('error', `❌ Error en propiedad ${propiedad.id || propiedad.ID}: ${error.message}`);
          fallidas++;
        }
      }

      try {
        await batch.commit();
        addLog('success', `✅ Lote ${Math.floor(i / batchSize) + 1} procesado: ${lote.length} propiedades`);
      } catch (error: any) {
        addLog('error', `❌ Error al guardar lote: ${error.message}`);
        fallidas += lote.length;
        exitosas -= lote.length;
      }
    }

    addLog('success', `✅ Propiedades completadas: ${exitosas} exitosas, ${fallidas} fallidas`);
    return { exitosas, fallidas, total: propiedadesData.length };
  };

  const ejecutarMigracion = async () => {
    const validaciones = {
      completa: csvAgencias && csvMiembros && csvPropiedades,
      agencias: csvAgencias,
      miembros: csvMiembros,
      propiedades: csvPropiedades
    };

    if (!validaciones[migrationMode]) {
      addLog('error', `❌ Debes cargar los archivos CSV necesarios para el modo ${migrationMode}`);
      return;
    }

    setIsRunning(true);
    setLogs([]);
    setResults(null);

    try {
      addLog('info', `🚀 Iniciando migración AMPI - Modo: ${migrationMode.toUpperCase()}`);
      addLog('info', `📅 Fecha: ${new Date().toLocaleString()}`);
      addLog('info', `👤 Usuario: ${user?.correo || 'Admin'}`);
      addLog('info', '');

      let resultadosAgencias = { exitosas: 0, fallidas: 0, total: 0 };
      let resultadosMiembros = { exitosos: 0, fallidos: 0, total: 0 };
      let resultadosPropiedades = { exitosas: 0, fallidas: 0, total: 0 };

      // Ejecutar fases según el modo seleccionado
      if (migrationMode === 'agencias' || migrationMode === 'completa') {
        const agenciasData = parseCSV(csvAgencias);
        addLog('info', `📊 ${agenciasData.length} agencias encontradas en CSV`);
        resultadosAgencias = await procesarAgencias(agenciasData);
        addLog('info', '');
      }

      if (migrationMode === 'miembros' || migrationMode === 'completa') {
        const miembrosData = parseCSV(csvMiembros);
        addLog('info', `📊 ${miembrosData.length} miembros encontrados en CSV`);
        resultadosMiembros = await procesarMiembros(miembrosData);
        addLog('info', '');
      }

      if (migrationMode === 'propiedades' || migrationMode === 'completa') {
        // Detectar si es JSON o CSV basándose en el primer carácter
        let propiedadesData: any[] = [];
        const firstChar = csvPropiedades.trim()[0];

        if (firstChar === '[' || firstChar === '{') {
          // Es JSON
          propiedadesData = parseJSON(csvPropiedades);
          addLog('info', `📊 ${propiedadesData.length} propiedades encontradas en JSON`);
        } else {
          // Es CSV
          propiedadesData = parseCSV(csvPropiedades);
          addLog('info', `📊 ${propiedadesData.length} propiedades encontradas en CSV`);
        }

        if (propiedadesData.length === 0) {
          addLog('warning', '⚠️ No se encontraron propiedades para migrar');
        }

        resultadosPropiedades = await procesarPropiedades(propiedadesData);
        addLog('info', '');
      }

      const resultado: MigrationResults = {
        agencias: resultadosAgencias,
        miembros: resultadosMiembros,
        propiedades: resultadosPropiedades
      };

      setResults(resultado);

      addLog('success', '═'.repeat(60));
      addLog('success', '🎉 MIGRACIÓN COMPLETADA EXITOSAMENTE');
      addLog('success', '═'.repeat(60));
      addLog('info', '');
      addLog('info', '📊 Resumen Final:');

      if (migrationMode === 'agencias' || migrationMode === 'completa') {
        addLog('success', `   🏢 Agencias: ${resultadosAgencias.exitosas}/${resultadosAgencias.total} exitosas`);
      }
      if (migrationMode === 'miembros' || migrationMode === 'completa') {
        addLog('success', `   👥 Miembros: ${resultadosMiembros.exitosos}/${resultadosMiembros.total} exitosos`);
      }
      if (migrationMode === 'propiedades' || migrationMode === 'completa') {
        addLog('success', `   🏠 Propiedades: ${resultadosPropiedades.exitosas}/${resultadosPropiedades.total} exitosas`);
      }

      addLog('info', '');
      addLog('info', '✅ Verifica los datos en Firebase Console');
      addLog('info', '✅ URL: https://console.firebase.google.com/u/0/project/ampi-riviera-system/firestore');

    } catch (error: any) {
      addLog('error', `💥 Error crítico en migración: ${error.message}`);
      addLog('error', error.stack || '');
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="container mt-4">
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header bg-primary text-white">
              <h3 className="mb-0">🏢 Migración AMPI Riviera Nayarit</h3>
              <small>Sistema optimizado para migración de datos limpios</small>
            </div>
            <div className="card-body">

              {/* Selector de modo de migración */}
              <div className="row mb-4">
                <div className="col-12">
                  <div className="card border-info">
                    <div className="card-header bg-info text-white">
                      <h5 className="mb-0">🎯 Modo de Migración</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-3">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="migrationMode"
                              id="completa"
                              checked={migrationMode === 'completa'}
                              onChange={() => setMigrationMode('completa')}
                              disabled={isRunning}
                            />
                            <label className="form-check-label" htmlFor="completa">
                              <strong>🔄 Completa</strong><br />
                              <small>Agencias + Miembros + Propiedades</small>
                            </label>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="migrationMode"
                              id="agencias"
                              checked={migrationMode === 'agencias'}
                              onChange={() => setMigrationMode('agencias')}
                              disabled={isRunning}
                            />
                            <label className="form-check-label" htmlFor="agencias">
                              <strong>🏢 Solo Agencias</strong><br />
                              <small>134 agencias</small>
                            </label>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="migrationMode"
                              id="miembros"
                              checked={migrationMode === 'miembros'}
                              onChange={() => setMigrationMode('miembros')}
                              disabled={isRunning}
                            />
                            <label className="form-check-label" htmlFor="miembros">
                              <strong>👥 Solo Miembros</strong><br />
                              <small>302 miembros</small>
                            </label>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="migrationMode"
                              id="propiedades"
                              checked={migrationMode === 'propiedades'}
                              onChange={() => setMigrationMode('propiedades')}
                              disabled={isRunning}
                            />
                            <label className="form-check-label" htmlFor="propiedades">
                              <strong>🏠 Solo Propiedades</strong><br />
                              <small>140 propiedades Riviera Nayarit</small>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Carga de archivos */}
              <div className="row mb-4">
                {(migrationMode === 'agencias' || migrationMode === 'completa') && (
                  <div className="col-md-4">
                    <div className="card">
                      <div className="card-header bg-info text-white">
                        <h5 className="mb-0">📋 Agencias (134)</h5>
                      </div>
                      <div className="card-body">
                        <input
                          type="file"
                          className="form-control"
                          accept=".csv"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleFileRead(file, setCsvAgencias);
                          }}
                          disabled={isRunning}
                        />
                        <small className="text-muted mt-2 d-block">
                          {csvAgencias ? '✅ agencias-limpias.csv cargado' : '⏳ Cargar agencias-limpias.csv'}
                        </small>
                      </div>
                    </div>
                  </div>
                )}

                {(migrationMode === 'miembros' || migrationMode === 'completa') && (
                  <div className="col-md-4">
                    <div className="card">
                      <div className="card-header bg-success text-white">
                        <h5 className="mb-0">👥 Miembros (302)</h5>
                      </div>
                      <div className="card-body">
                        <input
                          type="file"
                          className="form-control"
                          accept=".csv"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleFileRead(file, setCsvMiembros);
                          }}
                          disabled={isRunning}
                        />
                        <small className="text-muted mt-2 d-block">
                          {csvMiembros ? '✅ miembros-limpios.csv cargado' : '⏳ Cargar miembros-limpios.csv'}
                        </small>
                      </div>
                    </div>
                  </div>
                )}

                {(migrationMode === 'propiedades' || migrationMode === 'completa') && (
                  <div className="col-md-4">
                    <div className="card">
                      <div className="card-header bg-warning text-white">
                        <h5 className="mb-0">🏠 Propiedades (140)</h5>
                      </div>
                      <div className="card-body">
                        <input
                          type="file"
                          className="form-control"
                          accept=".json,.csv"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleFileRead(file, setCsvPropiedades);
                          }}
                          disabled={isRunning}
                        />
                        <small className="text-muted mt-2 d-block">
                          {csvPropiedades ? '✅ Archivo de propiedades cargado' : '⏳ Cargar propiedades-riviera-nayarit.json (o .csv)'}
                        </small>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Advertencia importante */}
              <div className="alert alert-warning mb-4">
                <h5 className="alert-heading">⚠️ Antes de migrar:</h5>
                <ol className="mb-0">
                  <li>Asegúrate de haber ejecutado <code>npm run backup:firebase</code></li>
                  <li>Ejecuta <code>npm run limpiar:firebase</code> para eliminar propiedades antiguas</li>
                  <li>Verifica que los archivos CSV estén en: <code>data-cleaned/</code></li>
                </ol>
              </div>

              {/* Botón de ejecución */}
              <div className="text-center mb-4">
                <button
                  className={`btn btn-lg ${isRunning ? 'btn-warning' : 'btn-success'}`}
                  onClick={ejecutarMigracion}
                  disabled={isRunning}
                  style={{ minWidth: '300px' }}
                >
                  {isRunning ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Migrando...
                    </>
                  ) : (
                    <>
                      🚀 INICIAR MIGRACIÓN {migrationMode.toUpperCase()}
                    </>
                  )}
                </button>
                {isRunning && (
                  <div className="mt-2">
                    <small className="text-warning">
                      ⚠️ No cierre esta ventana durante la migración
                    </small>
                  </div>
                )}
              </div>

              {/* Logs en tiempo real */}
              {logs.length > 0 && (
                <div className="card mb-4">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">📋 Logs de Migración</h5>
                    <span className="badge bg-secondary">{logs.length} entradas</span>
                  </div>
                  <div className="card-body" style={{ maxHeight: '500px', overflowY: 'auto', backgroundColor: '#1e1e1e' }}>
                    {logs.map((log, index) => {
                      const getLogColor = (type: string) => {
                        switch (type) {
                          case 'success': return '#4ade80';
                          case 'warning': return '#fbbf24';
                          case 'error': return '#f87171';
                          default: return '#60a5fa';
                        }
                      };

                      return (
                        <div
                          key={index}
                          style={{
                            color: getLogColor(log.type),
                            fontFamily: 'monospace',
                            fontSize: '13px',
                            marginBottom: '4px',
                            borderLeft: `3px solid ${getLogColor(log.type)}`,
                            paddingLeft: '8px'
                          }}
                        >
                          <span style={{ color: '#6b7280', marginRight: '10px' }}>
                            [{log.timestamp.toLocaleTimeString()}]
                          </span>
                          {log.message}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Resultados finales */}
              {results && (
                <div className="card border-success">
                  <div className="card-header bg-success text-white">
                    <h5 className="mb-0">📊 Resultados de la Migración</h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      {(migrationMode === 'agencias' || migrationMode === 'completa') && results.agencias.total > 0 && (
                        <div className="col-md-4">
                          <div className="card">
                            <div className="card-header bg-info text-white">
                              <h6 className="mb-0">🏢 Agencias</h6>
                            </div>
                            <div className="card-body">
                              <div className="row text-center">
                                <div className="col-6">
                                  <h3 className="text-success">{results.agencias.exitosas}</h3>
                                  <small>Exitosas</small>
                                </div>
                                <div className="col-6">
                                  <h3 className="text-danger">{results.agencias.fallidas}</h3>
                                  <small>Fallidas</small>
                                </div>
                              </div>
                              <hr />
                              <div className="text-center">
                                <strong>Total: {results.agencias.total}</strong>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {(migrationMode === 'miembros' || migrationMode === 'completa') && results.miembros.total > 0 && (
                        <div className="col-md-4">
                          <div className="card">
                            <div className="card-header bg-success text-white">
                              <h6 className="mb-0">👥 Miembros</h6>
                            </div>
                            <div className="card-body">
                              <div className="row text-center">
                                <div className="col-6">
                                  <h3 className="text-success">{results.miembros.exitosos}</h3>
                                  <small>Exitosos</small>
                                </div>
                                <div className="col-6">
                                  <h3 className="text-danger">{results.miembros.fallidos}</h3>
                                  <small>Fallidos</small>
                                </div>
                              </div>
                              <hr />
                              <div className="text-center">
                                <strong>Total: {results.miembros.total}</strong>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {(migrationMode === 'propiedades' || migrationMode === 'completa') && results.propiedades.total > 0 && (
                        <div className="col-md-4">
                          <div className="card">
                            <div className="card-header bg-warning text-white">
                              <h6 className="mb-0">🏠 Propiedades</h6>
                            </div>
                            <div className="card-body">
                              <div className="row text-center">
                                <div className="col-6">
                                  <h3 className="text-success">{results.propiedades.exitosas}</h3>
                                  <small>Exitosas</small>
                                </div>
                                <div className="col-6">
                                  <h3 className="text-danger">{results.propiedades.fallidas}</h3>
                                  <small>Fallidas</small>
                                </div>
                              </div>
                              <hr />
                              <div className="text-center">
                                <strong>Total: {results.propiedades.total}</strong>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="alert alert-info mt-4 mb-0">
                      <h6 className="alert-heading">✅ Próximos pasos:</h6>
                      <ol className="mb-0">
                        <li>Verificar conteos en <a href="https://console.firebase.google.com/u/0/project/ampi-riviera-system/firestore" target="_blank" rel="noreferrer">Firebase Console</a></li>
                        <li>Probar búsquedas en el frontend</li>
                        <li>Validar relaciones entre agencias, miembros y propiedades</li>
                      </ol>
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
